const moment = require('moment');
const { cloneDeep } = require('@/functions/clone');
const { formatDateForApi } = require('@/lib/Dates/dateFunctions');
const { getFieldValuesFromCart } = require('@/lib/CustomFields/fieldFunctions');
const { removeEmptyValues } = require('@/functions/objects');
const { removeNullValues } = require('@/functions/functions');
const { shouldGiftAidCart } = require('@/libraries/DigiTickets/GiftAid/giftAid');
const { unflatten } = require('digitickets.flatten-js');

/**
 * @typedef {Object} OrderConfirmationStructure
 * @property {?Object} customer
 * @property {?number} customerAccountID
 * @property {string} date
 * @property {?number} deviceID
 * @property {Object} fieldData
 * @property {number} force
 * @property {number} giftaid
 * @property {Object} memberships
 * @property {?string} notes
 * @property {number} orderBranchID
 * @property {Object[]} renewingMemberships
 * @property {?string} reservationToken
 * @property {number} sellersUserID
 * @property {?string} staffRef
 * @property {?number} thirdPartyID
 * @property {?string} thirdPartyRef
 * @property {?number} tradingSessionID
 * @property {number} total
 * @property {?Object} deliveryAddress
 * @property {?Object} deliveryContact
 * @property {PaymentForApiStructure[]} [payments]
 * @property {Object} [bookingGroups]
 */

/**
 * @typedef {OrderConfirmationStructure} OrderAdjustmentConfirmationStructure
 * @property {number} adjustedOrderID
 */

/**
 * @constructor
 *
 * @param {CartLineSplitter} cartLineSplitter
 * @param {CartToReservationStructureMapper} cartToReservationStructureMapper
 * @param {CurrentDevice} currentDevice
 * @param {MembershipService} membershipService
 * @param {PaymentService} paymentService
 * @param {ThirdPartyRefGenerator} thirdPartyRefGenerator
 * @param {UserService} userService
 */
const CartToConfirmationStructureMapper = function (
    cartLineSplitter,
    cartToReservationStructureMapper,
    currentDevice,
    membershipService,
    paymentService,
    thirdPartyRefGenerator,
    userService
) {
    /**
     * @type {CartLineSplitter}
     */
    this.cartLineSplitter = cartLineSplitter;

    /**
     * @type {CartToReservationStructureMapper}
     */
    this.cartToReservationStructureMapper = cartToReservationStructureMapper;

    /**
     * @type {CurrentDevice}
     */
    this.currentDevice = currentDevice;

    /**
     * @type {MembershipService}
     */
    this.membershipService = membershipService;

    /**
     * @type {PaymentService}
     */
    this.paymentService = paymentService;

    /**
     * @type {ThirdPartyRefGenerator}
     */
    this.thirdPartyRefGenerator = thirdPartyRefGenerator;

    /**
     * @type {UserService}
     */
    this.userService = userService;
};

CartToConfirmationStructureMapper.prototype = {
    /**
     * Convert a cart to the structure required for the order creation API endpoint.
     *
     * @param {Cart} cart
     *
     * @returns {OrderConfirmationStructure}
     */
    map(cart) {
        // We need to record when the EPOS (thinks it) created the order, not leave
        // it to the server to decide - might be hours later that it receives it.
        const createdDate = new Date();
        const createdDateString = formatDateForApi(createdDate);

        // If some customer info has been entered, but a country has not been selected, and we have a default country
        // from the customer, set the customer's country to that.
        if (cart.customer.hasData() && !cart.customer.address.country && this.userService.company.country) {
            cart.customer.address.country = this.userService.company.country;
        }

        const thirdPartyRef = this.thirdPartyRefGenerator.generateOrderThirdPartyRef(cart);

        let structure = {
            customer: cart.customer.account ? null : cart.customer.toServerData(),
            customerAccountID: cart.customer.account ? cart.customer.account.ID : null,
            date: createdDateString,
            deviceID: this.currentDevice.isSet() ? this.currentDevice.getDeviceId() : null,
            fieldData: this.getFieldValuesFromCartForApi(cart),
            // TODO: find out if with robust validation in the API will we be able to remove this?
            // Force the order to pass/skip validation on the API
            force: 1,
            giftaid: shouldGiftAidCart(cart) ? 1 : 0,
            groupBooking: cart.groupBooking ? 1 : 0,
            memberships: this.membershipService.getDataForServer(cart.itemList),
            notes: cart.notes,
            orderBranchID: this.userService.getOrderBranchID(),

            // Payments are no longer included in the confirmation structure.
            // TODO: https://digitickets.atlassian.net/browse/PROP-2503
            // They are back (added below).

            // We include all the membership renewals in a separate data block ("renewingMemberships") here instead
            // of in the order lines because the order lines are sent ahead as a reservation, and we'd have to do
            // a huge amount of work to add 1 or 2 columns/attributes to the table/models.
            renewingMemberships: this.getRenewingMembershipDataFromCartForApi(cart),
            reservationToken: cart.reservation && cart.reservation.token ? cart.reservation.token : null,
            sellersUserID: this.userService.getUserID(),
            staffRef: cart.staffRef,
            thirdPartyID: thirdPartyRef,
            thirdPartyRef,
            tradingSessionID: this.currentDevice.getTradingSessionID(),
            total: cart.getTotal(),
            bookingGroups: cart.bookingGroups,
        };

        if (cart.customer.deliveryAddress && !cart.customer.deliveryAddress.isEmpty()) {
            structure.deliveryAddress = cart.customer.deliveryAddress.toServerData();
        }

        if (cart.customer.deliveryContact && !cart.customer.deliveryContact.isEmpty()) {
            structure.deliveryContact = cart.customer.deliveryContact.toServerData();
        }

        // TODO: https://digitickets.atlassian.net/browse/PROP-2503
        // We tried to remove payments from the order creation request and send them separately.
        // But this caused several issues (see the Jira), so they are back for now.
        const payments = this.paymentService.getAllNewPaymentsFromCart(cart);
        structure.payments = this.paymentService.convertPaymentsToApiStructure(payments);

        removeNullValues(structure);

        return structure;
    },

    /**
     * Produce an object in the structure specified in OE-107
     * for sending to the API endpoint to create an order adjustment.
     *
     * @param {Cart} cart
     *
     * @return {OrderAdjustmentConfirmationStructure}
     */
    mapOrderAdjustment(cart) {
        if (!cart.originalOrder) {
            throw new Error('There is no originalOrder in the cart.');
        }

        let confirmationStructure = this.map(cart);
        // Adjustments send the lines with the order - add those.
        confirmationStructure.lines = this.cartToReservationStructureMapper.mapLines(cart);

        // Add fields that used to be added in this.map prior to PROP-1695
        // TODO: Remove paid and paidAt once the API has been updated to support the new field names.
        confirmationStructure.paid = 1;
        confirmationStructure.paidAt = confirmationStructure.date;
        confirmationStructure.confirmed = 1;
        confirmationStructure.confirmedAt = confirmationStructure.date;
        confirmationStructure.requestAutoRedeem = 1;

        confirmationStructure = this.processModifiedLinesAndFieldData(confirmationStructure);
        confirmationStructure = this.processNewLinesAndFieldData(confirmationStructure);
        confirmationStructure = this.processZeroQtyLinesAndFieldData(confirmationStructure);
        confirmationStructure = this.processOrderLevelFieldData(confirmationStructure, cart.originalOrder.getOrderLevelFieldGroup());

        // Add adjustedOrderID to order data.
        confirmationStructure.adjustedOrderID = cart.originalOrder.ID;

        if (this.isAdjustmentWithoutLines(cart)) {
            confirmationStructure.adjustmentWithoutLines = 1;
        }

        return confirmationStructure;
    },

    /**
     * Utility method to clean up calling code when we could be dealing with either a new order or an adjustment.
     *
     * @param {Cart} cart
     *
     * @returns {Object}
     */
    mapNewOrderOrAdjustment(cart) {
        if (cart.originalOrder) {
            return this.mapOrderAdjustment(cart);
        }
        return this.map(cart);
    },

    /**
     * Check if there are any active order level fields in the originalOrder that don't exist in the new order.
     * If there are any then send these as empty strings to the API, so they get removed.
     *
     * Remember that there will only be field instances that had values in the original order, so we won't
     * be unnecessarily processing fields with no values here.
     *
     * @param {OrderConfirmationStructure} structure
     *
     * @param {FieldGroup} originalOrderLevelFieldGroup
     *
     * @returns {OrderConfirmationStructure}
     */
    processOrderLevelFieldData(structure, originalOrderLevelFieldGroup) {
        const setFieldValueInNewStructure = (fieldID, value) => {
            if (!structure.fieldData) {
                structure.fieldData = {};
            }
            if (!structure.fieldData.order) {
                structure.fieldData.order = {};
            }
            structure.fieldData.order[fieldID] = value;
        };

        originalOrderLevelFieldGroup.instances.forEach((instance) => {
            // When you send field data as part of an order adjustment it will *replace* the existing field data.
            // That means if a value is missing from the new structure it will be removed from the order.
            // This is unlike the PATCH /orders/field-data endpoint. With that endpoint only the fieldIDs you
            // supply in the request will be modified. All other field data would remain unchanged.

            // If we don't send any fieldData.order key to the adjustments endpoint, nothing will change about
            // the order level field data.
            // However, if we send an empty object, all order level field data will be removed/replaced.

            // Handle non-active fields.
            if (!instance.field.isActive()) {
                // The user can't have interacted with any non-active fields.
                // Ensure the data for these is retained by copying their values over to the new structure.
                setFieldValueInNewStructure(instance.field.ID, instance.value);
                return;
            }

            // Otherwise, for active fields...
            // If this field that had a value in the original order no longer has a value in the new order,
            // send its value as an empty string to cause the previous value to be cleared.
            if (!structure.fieldData?.order?.hasOwnProperty(instance.field.ID)) {
                setFieldValueInNewStructure(instance.field.ID, '');
            }
        });

        return structure;
    },

    /**
     * Send modified lines to the end of the confirmationStructure and re-key fieldData accordingly.
     *
     * @param {OrderConfirmationStructure} structure
     *
     * @returns {OrderConfirmationStructure}
     */
    processModifiedLinesAndFieldData(structure) {
        return this.lineAndFieldDataOrderer(structure, (line) => 'adjustedLineID' in line);
    },

    /**
     * Send new lines to the end of the confirmationStructure and re-key fieldData accordingly.
     *
     * @param {OrderConfirmationStructure} structure
     *
     * @returns {OrderConfirmationStructure}
     */
    processNewLinesAndFieldData(structure) {
        return this.lineAndFieldDataOrderer(structure, (line) => line.qtyBeforeAdjustment === 0);
    },

    /**
     * Move lines where qty is 0 to the end and re-key fieldData accordingly. If 0 qty lines do not have a
     * returnReasonID attached then we can just delete them as we don't need to send them to the API.
     *
     * @param {OrderConfirmationStructure} structure
     *
     * @returns {OrderConfirmationStructure}
     */
    processZeroQtyLinesAndFieldData(structure) {
        return this.lineAndFieldDataOrderer(structure, (line) => line.qty === 0, true);
    },

    /**
     * Reorder the lines and corresponding fieldData using the supplied function.
     *
     * We need to reorder the lines and fieldData in the confirmation structure when we are making an order adjustment
     * because lines get reordered by the API which breaks the link with fieldData. If we reorder the lines here so that
     * they don't need to get reordered by the API then the fieldData will continue to match.
     *
     * When we send the confirmationStructure to the API the order of lines should be:
     * - Unchanged lines
     * - Modified lines
     * - New lines
     * - Deleted lines with a returnReasonID
     *
     * @param {OrderConfirmationStructure} structure
     * @param {Function} comparisonFunction A function that returns true if the line should be moved.
     * @param {Boolean} deleteLines Should moved lines be deleted?
     *
     * @returns {OrderConfirmationStructure}
     */
    lineAndFieldDataOrderer(structure, comparisonFunction, deleteLines = false) {
        let fieldData = {};
        let fieldDataToMove = {};
        let standardLines = [];
        let standardLinesIndex = 0;
        let linesToMove = [];
        let linesToMoveIndex = 0;

        // Split the lines and fieldData into two groups: lines to move to the end and lines that stay in order.
        Object.keys(structure.lines).forEach((key) => {
            let line = structure.lines[key];
            if (comparisonFunction(line)) {
                linesToMove.push(line);
                if (structure.fieldData.line && structure.fieldData.line[key]) {
                    fieldDataToMove[linesToMoveIndex] = structure.fieldData.line[key];
                }
                linesToMoveIndex++;
            } else {
                standardLines.push(line);
                if (structure.fieldData.line && structure.fieldData.line[key]) {
                    fieldData[standardLinesIndex] = structure.fieldData.line[key];
                }
                standardLinesIndex++;
            }
        });

        structure.fieldData.line = fieldData;
        structure.lines = {};

        let index = 0;
        standardLines.forEach((line) => {
            structure.lines[index] = line;
            index++;
        });

        // Now deal with the lines we have moved. We can either move them to the end of lines or fieldData respectively,
        // or just throw them away.
        linesToMoveIndex = 0;
        linesToMove.forEach((line) => {
            // Even if we would throw a line away (because it has qty 0), keep it if it has a return reason attached.
            if (!deleteLines || line.returnReasonID) {
                structure.lines[index] = line;
                if (line.qty > 0 && fieldDataToMove[linesToMoveIndex]) {
                    structure.fieldData.line[index] = fieldDataToMove[linesToMoveIndex];
                }
                index++;
            }
            linesToMoveIndex++;
        });

        return structure;
    },

    /**
     * Return the field values from the cart in the structure expected by the API.
     *
     * @param {Cart} cart
     *
     * @return {Object<string, string|number|number[]>}
     */
    getFieldValuesFromCartForApi(cart) {
        let lines = this.cartLineSplitter.splitCartLinesForApi(cart);

        let cartWithSplitLines = cloneDeep(cart);
        cartWithSplitLines.itemList = lines;

        return this.convertFieldValuesForApi(
            getFieldValuesFromCart(cartWithSplitLines),
            lines
        );
    },

    /**
     * @param {Cart} cart
     *
     * @returns {*[]}
     */
    getRenewingMembershipDataFromCartForApi(cart) {
        let result = [];

        for (let i = 0; i < cart.itemList.length; i++) {
            let cartItem = cart.itemList[i];
            if (cartItem.renewingMembership) {
                result.push({
                    planId: cartItem.item.ID,
                    // These are arrays because the API expects them to be.
                    membershipIds: [cartItem.renewingMembership.ID],
                    renewalFromDates: [moment(cartItem.renewingMembershipFromDate).format('YYYY-MM-DD')],
                });
            }
        }

        return result;
    },

    /**
     * @param {Cart} cart
     *
     * @return {boolean}
     */
    isAdjustmentWithoutLines(cart) {
        // We can add additional reasons not to have lines here as required.
        if (cart.isOrderAdjustmentRefund()) {
            return true;
        }
        return false;
    },

    /**
     * Return the field values from the cart in the structure expected by the API.
     *
     * When sending the values to the API we don't send:
     * line.~$lineNumber.~$itemInstance.~$fieldID: value
     * e.g.
     * line.~1.~1.~100: 'kittens'
     *
     * we have to send:
     * line[lineNumber][itemInstance][fieldID]: value
     * e.g.
     * line[0][1][100]: 'kittens''
     *
     * We also need to replace the line numbers used in that array to not be the 'lineNumber' property on the lines
     * but instead be the index of the line in the array of lines.
     *
     * @param {Object<string, string|number|number[]>} fieldValues
     * @param {CartLine[]} lines
     *
     * @return {Object}
     */
    convertFieldValuesForApi(fieldValues, lines) {
        removeEmptyValues(fieldValues);

        // Convert fieldValues['item.~1.~1.~100'] to fieldValues['item'][1][1][100]
        fieldValues = unflatten(fieldValues, null, null, '.', false);

        // Mapping of the 'lineNumber' property in the cart to the index of that line in the lines array sent
        // to the API. The API expects the "line numbers" for field data to actually be an index.
        let lineIndexes = {};
        Object.keys(lines).forEach((key) => {
            let line = lines[key];
            lineIndexes[line.lineNumber] = key;
        });

        let adjustedFieldValues = {};

        if (fieldValues.order) {
            // Just put the order level data directly in the result because we don't need to do anything with it.
            adjustedFieldValues.order = fieldValues.order;
        }

        if (fieldValues.line) {
            adjustedFieldValues.line = {};
            // Line level field data needs its lineNumber adjusting.
            Object.keys(fieldValues.line).forEach((oldLineNumber) => {
                // Get the new line number.
                let lineIndex = lineIndexes[oldLineNumber];
                adjustedFieldValues.line[lineIndex] = cloneDeep(fieldValues.line[oldLineNumber]);
            });
        }

        return adjustedFieldValues;
    },
};

module.exports = CartToConfirmationStructureMapper;
